import React, { useMemo, useState } from 'react'
import { X, BookMarked, Loader2, Search } from 'lucide-react'
import { listCurriculum, fetchInheritedLesson } from '../integrations'
import { lessonStorage } from '../lib/lessonStorage'
import { genId } from '../lib/id'
import { makeSection } from '../lib/sections'
import type { LessonDoc } from '../types/LessonDoc'

// Cửa sổ chọn bài từ chương trình hiện có để tạo bài giảng mới (kế thừa nội dung).
const InheritCurriculumModal: React.FC<{ onClose: () => void; onCreated: (doc: LessonDoc) => void }> = ({
  onClose,
  onCreated,
}) => {
  const all = useMemo(() => listCurriculum(), [])
  const [lop, setLop] = useState<number | 0>(0)
  const [q, setQ] = useState('')
  const [loadingPath, setLoadingPath] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const filtered = all.filter(
    (it) => (lop === 0 || it.lop === lop) && (!q || it.title.toLowerCase().includes(q.toLowerCase()))
  )

  const handlePick = async (path: string) => {
    setLoadingPath(path)
    setError(null)
    try {
      const item = all.find((i) => i.path === path)!
      const inh = await fetchInheritedLesson(path)
      const doc: LessonDoc = {
        id: genId('lesson'),
        slug: '',
        meta: {
          tieuDe: inh.title,
          moTa: inh.description,
          lop: String(item.lop),
          chuong: String(item.chuong),
          bai: String(item.bai),
        },
        sections: [
          makeSection('Hoạt động mở đầu'),
          makeSection('Hoạt động chính', [{ id: genId(), type: 'rich', items: inh.items, sourceRef: path }]),
          makeSection('Hoạt động kết thúc'),
        ],
        status: 'draft',
      }
      const saved = lessonStorage.save(doc)
      onCreated(saved)
    } catch (e) {
      setError((e as Error)?.message || 'Không tải được bài gốc')
    } finally {
      setLoadingPath(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <BookMarked className="text-sky-600" size={20} />
            <h2 className="text-lg font-black text-gray-900">Kế thừa từ chương trình hiện có</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={22} />
          </button>
        </div>

        <div className="p-4 flex items-center gap-2 border-b border-gray-50">
          <div className="flex items-center gap-2 flex-1 px-3 py-2 rounded-lg bg-gray-50">
            <Search size={16} className="text-gray-400" />
            <input
              className="flex-1 bg-transparent outline-none text-sm"
              placeholder="Tìm bài học..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none"
            value={lop}
            onChange={(e) => setLop(Number(e.target.value))}
          >
            <option value={0}>Tất cả lớp</option>
            <option value={10}>Lớp 10</option>
            <option value={11}>Lớp 11</option>
            <option value={12}>Lớp 12</option>
          </select>
        </div>

        {error && <p className="px-4 pt-3 text-sm text-red-600">{error}</p>}

        <div className="overflow-y-auto p-3 space-y-1.5">
          {filtered.map((it) => (
            <button
              key={it.path}
              onClick={() => handlePick(it.path)}
              disabled={!!loadingPath}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-sky-200 hover:bg-sky-50/40 transition-all text-left disabled:opacity-50"
            >
              <span className="text-[10px] font-black text-gray-400 w-16 shrink-0">
                L{it.lop} · C{it.chuong} · B{it.bai}
              </span>
              <span className="flex-1 text-sm font-semibold text-gray-800">{it.title}</span>
              {loadingPath === it.path && <Loader2 size={16} className="animate-spin text-sky-500" />}
            </button>
          ))}
          {filtered.length === 0 && <p className="text-center text-gray-400 text-sm py-8">Không tìm thấy bài học.</p>}
        </div>
      </div>
    </div>
  )
}

export default InheritCurriculumModal
